import { useState, useRef, useCallback, useEffect, DragEvent, ChangeEvent } from "react";
import Webcam from "react-webcam";
import { preload as preloadBackgroundRemoval } from "@imgly/background-removal";
import EmptyState from "@/components/EmptyState";
import Progress from "@/components/Progress";
import IconButton from "@/components/IconButton";
import { usePlatformDetection } from "@/hooks/usePlatformDetection";
import type { PhotoboothCaptureMethod } from "@/pages/photobooth/_utils/amplitude";

interface CameraContentProps {
  onCapture: (imageData: string, captureMethod: PhotoboothCaptureMethod) => void;
  onBack: () => void;
}

type CameraState = "loading" | "ready" | "error" | "denied";

type FacingMode = "user" | "environment";

export default function CameraContent({ onCapture, onBack }: CameraContentProps) {
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraState, setCameraState] = useState<CameraState>("loading");
  const [silhouetteVisible, setSilhouetteVisible] = useState(true);
  const [facingMode, setFacingMode] = useState<FacingMode>("user");
  const [isDragging, setIsDragging] = useState(false);
  const { isMobile, isDesktop } = usePlatformDetection();

  useEffect(() => {
    preloadBackgroundRemoval({ model: "isnet" }).catch(() => {});
  }, []);

  const stopCamera = useCallback(() => {
    const stopStream = (stream: MediaStream | null | undefined) => {
      if (!stream) return;
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    };

    stopStream(streamRef.current);
    streamRef.current = null;

    stopStream(webcamRef.current?.stream ?? null);
    if (webcamRef.current?.stream) {
      webcamRef.current.stream = null;
    }

    if (webcamRef.current?.video?.srcObject) {
      const video = webcamRef.current.video;
      stopStream(video.srcObject as MediaStream);
      video.srcObject = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const handleUserMedia = useCallback((stream: MediaStream) => {
    if (streamRef.current && streamRef.current !== stream) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = stream;
    setCameraState("ready");
  }, []);

  const handleUserMediaError = useCallback((error: string | DOMException) => {
    if (error instanceof DOMException && error.name === "NotAllowedError") {
      setCameraState("denied");
    } else {
      setCameraState("error");
    }
  }, []);

  const handleCapture = useCallback(() => {
    if (!webcamRef.current?.video) return;

    const video = webcamRef.current.video;
    const { videoWidth, videoHeight } = video;
    if (!videoWidth || !videoHeight) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoWidth;
    canvas.height = videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (facingMode === "user") {
      ctx.translate(videoWidth, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

    stopCamera();
    onCapture(canvas.toDataURL("image/jpeg", 0.95), "camera");
  }, [onCapture, facingMode, stopCamera]);

  const handleBack = useCallback(() => {
    stopCamera();
    onBack();
  }, [stopCamera, onBack]);

  const handleToggleSilhouette = useCallback(() => {
    setSilhouetteVisible((prev) => !prev);
  }, []);

  const handleSwitchCamera = useCallback(() => {
    stopCamera();
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, [stopCamera]);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) {
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        stopCamera();
        onCapture(reader.result as string, "upload");
      };
      reader.readAsDataURL(file);
    },
    [onCapture, stopCamera]
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      event.target.value = "";
    },
    [handleFileSelect]
  );

  const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const file = event.dataTransfer?.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const videoConstraints: MediaTrackConstraints = {
    width: { ideal: 1536, min: 1440 },
    height: { ideal: 1152, min: 1080 },
    aspectRatio: { ideal: 4 / 3 },
    facingMode
  };

  const screenshotDimensions = {
    width: 1536,
    height: 1152
  };

  if (cameraState === "loading") {
    return (
      <div className="flex flex-col items-center justify-center py-12" data-testid="camera-loading">
        <Progress />
        <p className="text-sys-brand-on-surface-variant mt-4 text-body-medium">Initializing camera...</p>
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          screenshotQuality={0.95}
          videoConstraints={videoConstraints}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleUserMediaError}
          forceScreenshotSourceSize={false}
          minScreenshotWidth={screenshotDimensions.width}
          minScreenshotHeight={screenshotDimensions.height}
          className="hidden"
        />
      </div>
    );
  }

  if (cameraState === "denied") {
    return (
      <div className="py-8">
        <EmptyState
          iconName="videocam_off"
          title="Camera Access Denied"
          caption="Please allow camera access in your browser settings to use the camera."
          action={{
            children: "Go Back",
            icon: "arrow_back",
            onClick: handleBack
          }}
        />
      </div>
    );
  }

  if (cameraState === "error") {
    return (
      <div className="py-8">
        <EmptyState
          iconName="error"
          title="Camera Unavailable"
          caption="We couldn't access your camera. Please make sure it's connected and not being used by another application."
          action={{
            children: "Go Back",
            icon: "arrow_back",
            onClick: handleBack
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="camera-content">
      <div className="relative bg-black rounded-lg overflow-hidden aspect-[4/3]">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          screenshotQuality={0.95}
          videoConstraints={videoConstraints}
          onUserMedia={handleUserMedia}
          onUserMediaError={handleUserMediaError}
          forceScreenshotSourceSize={false}
          minScreenshotWidth={screenshotDimensions.width}
          minScreenshotHeight={screenshotDimensions.height}
          className="w-full h-full object-cover"
          mirrored={facingMode === "user"}
        />

        {silhouetteVisible && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-1/2 aspect-[3/4] border-2 border-white/50 rounded-lg" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-4">
        <IconButton
          className="!m-0"
          onClick={handleUploadClick}
          name="photo_library"
          title={isMobile ? "Upload Photo" : "Upload from Computer"}
        />

        <IconButton
          className="!m-0 [--md-filled-icon-button-container-width:64px] [--md-filled-icon-button-container-height:64px]"
          iconClassName="text-sys-brand-on-primary"
          onClick={handleCapture}
          name="photo_camera"
          variant="filled"
          title="Take photo"
          data-testid="capture-button"
        />

        <IconButton
          className="!m-0"
          onClick={handleToggleSilhouette}
          name="person"
          variant={silhouetteVisible ? "filled" : "default"}
          title={silhouetteVisible ? "Hide guide" : "Show guide"}
        />

        {isMobile && (
          <IconButton className="!m-0" onClick={handleSwitchCamera} name="cameraswitch" title="Switch camera" />
        )}
      </div>

      {isDesktop && (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-4 text-center transition-all
            ${
              isDragging
                ? "border-sys-brand-primary bg-sys-brand-primary/5"
                : "border-sys-brand-outline hover:border-sys-brand-primary"
            }
          `}
        >
          <p className="text-body-small text-sys-brand-on-surface-variant">
            {isDragging ? "Drop image here" : "Or drag and drop an image"}
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="file-upload-input"
      />
    </div>
  );
}
